<template>
    <div class="lazy-image-wrapper">
        <!-- Always render img to maintain layout, control visibility with opacity -->
        <img
            ref="imgElement"
            :src="imageSrc"
            :alt="alt"
            :class="imgClass"
            :style="{ opacity: (!loading && !error) ? 1 : 0 }"
            cache="false"
            @load="onLoad"
            @error="onError"
        />

        <!-- Show skeleton loader while image is loading -->
        <div v-if="loading" class="skeleton-loader" :class="imgClass"></div>

        <!-- Show error state if image fails to load -->
        <div v-if="error" class="error-placeholder" :class="imgClass">
            <mdicon name="image-off" size="24" />
        </div>
    </div>
</template>

<script>
import { db } from '@/services/db';

export default {
    name: 'LazyImage',
    props: {
        uuid: {
            type: String,
            required: true
        },
        alt: {
            type: String,
            default: ''
        },
        imgClass: {
            type: String,
            default: 'image-class'
        }
    },
    data() {
        return {
            imageSrc: '', // Start with empty src
            loading: true,
            error: false,
            loaded: false, // Track if image successfully loaded
            loadTimeout: null // Store timeout reference for cleanup
        }
    },
    mounted() {
        // Load image immediately when component mounts
        this.loadImage();
    },
    beforeUnmount() {
        // CRITICAL: Clear image src to free memory
        console.log('[LazyImage] beforeUnmount - freeing memory for', this.uuid);

        // Clear timeout to prevent memory leak
        if (this.loadTimeout) {
            clearTimeout(this.loadTimeout);
            this.loadTimeout = null;
        }

        this.imageSrc = '';

        // Remove src attribute to force browser to release image from memory
        if (this.$refs.imgElement) {
            this.$refs.imgElement.src = '';
            this.$refs.imgElement.removeAttribute('src');
        }
    },
    watch: {
        // If uuid changes, reload the image
        uuid: {
            handler(newUuid, oldUuid) {
                if (newUuid !== oldUuid && newUuid) {
                    console.log('[LazyImage] UUID changed, reloading');

                    // Clear any existing timeout before reloading
                    if (this.loadTimeout) {
                        clearTimeout(this.loadTimeout);
                        this.loadTimeout = null;
                    }

                    this.imageSrc = '';
                    this.loaded = false; // Reset loaded flag
                    this.error = false;
                    this.loading = true;
                    this.loadImage();
                }
            },
            immediate: false
        }
    },
    methods: {
        async loadImage() {
            if (!this.uuid) {
                console.warn('[LazyImage] No UUID provided');
                return;
            }

            this.loading = true;
            this.error = false;

            try {
                // Load ONLY this specific image from IndexedDB
                const image = await db.images.get(this.uuid);

                if (image) {
                    // SMART LOADING: Prefer url when online, base64 when offline
                    const isOnline = this.$store?.getters?.online ?? navigator.onLine;

                    let imageSource = null;

                    if (isOnline && image.url) {
                        // Online: Use URL (browser caches it, saves memory)
                        imageSource = image.url;
                        console.log('[LazyImage] Using URL (online):', this.uuid);
                    } else if (image.base64) {
                        // Offline or no URL: Use base64
                        imageSource = image.base64;
                        console.log('[LazyImage] Using base64 (offline):', this.uuid);
                    }

                    if (imageSource) {
                        this.imageSrc = imageSource;

                        // Fallback: If @load doesn't fire within 1.5 seconds, show image anyway
                        // This handles cases where load event doesn't fire (CORS, cached images, etc.)
                        this.loadTimeout = setTimeout(() => {
                            if (this.loading && !this.error) {
                                console.log('[LazyImage] Load timeout, showing image anyway:', this.uuid);
                                this.loading = false;
                            }
                        }, 1500);

                        // Check if image is already loaded (cached by browser)
                        this.$nextTick(() => {
                            if (this.$refs.imgElement && this.$refs.imgElement.complete) {
                                console.log('[LazyImage] Image already loaded (cached):', this.uuid);
                                this.loading = false;
                            }
                        });
                    } else {
                        console.warn('[LazyImage] No image source available for UUID:', this.uuid);
                        this.error = true;
                        this.loading = false;
                    }

                    // The 'image' object from IndexedDB goes out of scope here
                    // and can be garbage collected immediately
                } else {
                    console.warn('[LazyImage] No image found for UUID:', this.uuid);
                    this.error = true;
                    this.loading = false;
                }
            } catch (err) {
                console.error('[LazyImage] Failed to load image:', err);
                this.error = true;
                this.loading = false;
            }
        },
        onLoad() {
            console.log('[LazyImage] Image loaded successfully:', this.uuid, 'loading:', this.loading, 'error:', this.error);

            // Clear timeout since image loaded successfully
            if (this.loadTimeout) {
                clearTimeout(this.loadTimeout);
                this.loadTimeout = null;
            }

            this.loaded = true; // Mark as successfully loaded
            this.loading = false;
            this.error = false; // Clear any error state
            console.log('[LazyImage] After setting loading=false:', this.uuid, 'loading:', this.loading);
            this.$emit('load');
        },
        onError(event) {
            // Only set error if image hasn't already loaded successfully
            // (prevents race condition where @error fires after @load)
            // Also ignore errors if we haven't set a valid src yet (initial empty src)
            if (!this.loaded && this.imageSrc) {
                this.error = true;
                this.loading = false;
                console.error('[LazyImage] Image failed to load:', this.uuid, event);
                this.$emit('error');
            } else {
                console.log('[LazyImage] Ignoring error event:', this.uuid, 'loaded:', this.loaded, 'hasSrc:', !!this.imageSrc);
            }
        }
    }
};
</script>

<style scoped>
.lazy-image-wrapper {
    position: relative;
    display: inline-block;
}

.skeleton-loader {
    position: absolute;
    top: 0;
    left: 0;
    background: linear-gradient(90deg,
        var(--ion-color-medium-tint) 0%,
        var(--ion-color-light) 50%,
        var(--ion-color-medium-tint) 100%);
    background-size: 200% 100%;
    animation: shimmer-effect 1.5s infinite ease-in-out;
    pointer-events: none;
}

@keyframes shimmer-effect {
    0% {
        background-position: 100% 0;
    }
    100% {
        background-position: -100% 0;
    }
}

.error-placeholder {
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #f5f5f5;
    color: #999;
}
</style>
